import { FIRST } from '../constants/pagination.js';
import {
  getAllDevicesCounter,
  getDeviceByIdQuery,
  getDevicesInGroupQuery,
  getDevicesInitCounters,
  getDevicesWithoutAggregate,
  getFieldsQuery,
  getGroupsQuery,
  getSearchResult,
  getInitialDevicesInGroupQuery,
  getDeviceLicenseCountQuery,
  getAllDeviceLabels,
  searchGroups,
  UpdateDevicesQuery,
} from '../utils/graphql/queries.js';

/**
 * @typedef { import('unattended-iot-client/dist/types/device').Device } Device
 * @typedef { import('synapse-common-device-service').Group } Group
 * @typedef { import('synapse-common-device-service').DeviceFilters } DeviceFilters
 * @typedef { import('synapse-common-device-service').Pagination } Pagination
 * @typedef { import('synapse-common-device-service').AggregateCounters } AggregateCounters
 * @typedef { import('synapse-common-device-service').UpdateDeviceInput } UpdateDeviceInput
 * @typedef { Pick<UpdateDeviceInput, 'id' | 'deviceName' | 'labels'> } UpdateDeviceByUserInput
 */
export default class CommonDeviceService {
  /**
   *
   * @param {string} jwtToken
   * @param {string} endpoint
   */
  constructor(jwtToken, endpoint) {
    /** @type {string} */
    this.token = jwtToken;

    /** @type {string} */
    this.endpoint = endpoint;
  }

  get authorization() {
    return `Bearer ${this.token}`;
  }

  get headers() {
    return {
      authorization: this.authorization,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  /**
   *
   * @param {string} newJwt
   */
  updateToken(newJwt) {
    this.token = newJwt;
  }

  /**
   *
   * @param {string} query
   * @param {object} variables
   * @param {...string} methods
   */
  async request(query, variables = {}, ...methods) {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { ...this.headers },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      if (!response.ok) {
        throw Error(`Error happened: ${response.statusText}, ${response.status}`);
      }

      const { data, errors } = await response.json();

      if (errors) {
        console.log(`Errors in the request: ${JSON.stringify(errors)}`);
      }

      return methods.length === 1 ? data[methods[0]] : data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  /**
   * @param {string} query
   * @returns {Promise<import('synapse-common-device-service').Group[]>}
   */
  async getGroups(query = getGroupsQuery) {
    return this.request(query, {}, 'getGroups');
  }

  /**
   *
   * @param {DeviceFilters} filters
   * @param {Pagination} pagination
   * @returns {Promise<{ devices: Device[], zeroTrustId: string, aggregate: { totalDeviceCount: number } }>}
   */
  async getGroupDevices(filters, pagination = {}) {
    const variables = {
      ...filters,
      ...pagination,
    };

    return this.request(getDevicesInGroupQuery, variables, 'getGroup');
  }

  /**
   *
   * @param {string} zeroTrustId
   * @returns {Promise<Group>}
   */
  async getInitialDevicesByGroupId(zeroTrustId) {
    const variables = { zeroTrustId, sort: { status: 'desc' }, first: FIRST, offset: 0 };

    return this.request(getInitialDevicesInGroupQuery, variables, 'getGroup');
  }

  /**
   *
   * @param {DeviceFilters} filters
   * @param {Pagination} pagination
   * @param {string?} query
   * @returns {Promise<{
   *  getDevices: Device[]
   *  getAggregate: AggregateCounters
   * }>}
   */
  async getDevices(filters, pagination = {}, query = getFieldsQuery()) {
    const variables = {
      ...filters,
      ...pagination,
    };
    const data = await this.request(query, variables, 'getDevices', 'getAggregateBy');
    return {
      getDevices: data.getDevices,
      getAggregate: data.getAggregateBy[0]?.aggregate || {},
    };
  }

  /**
   *
   * @param {DeviceFilters} filters
   * @returns {Promise<{
   *  getDevices: Device[]
   *   getGroups: Group[]
   * }>}
   */
  async remoteSearchDevicesAndGroups(filters) {
    const variables = { ...filters };
    return this.request(getSearchResult, variables, 'getDevices', 'getGroups');
  }

  /**
   *
   * @param {{ searchText: string }} variable
   * @returns {Promise<Group[]>}
   */
  async remoteSearchGroups(variable) {
    return this.request(searchGroups, variable, 'getGroups');
  }

  /**
   *
   * @param {string} deviceId
   * @returns {Promise<Device[]>}
   */
  async getDeviceById(deviceId) {
    const variables = { deviceId };

    return this.request(getDeviceByIdQuery, variables, 'getDevice');
  }

  /**
   *
   * @returns {Promise<import('synapse-common-device-service').Aggregate>}
   */
  async getInitCounters() {
    const data = await this.request(getDevicesInitCounters, {}, 'getAggregateBy');
    return data?.[0]?.aggregate;
  }

  /**
   * @param {DeviceFilters} filters
   * @returns {Promise<number>}
   */
  async getAllTotalCount(filters) {
    const data = await this.request(getAllDevicesCounter, filters, 'getAggregateBy');

    return data[0].aggregate.totalDeviceCount;
  }

  /**
   *
   * @returns {Promise<string[]>}
   */
  async getAllDeviceLabels() {
    const data = await this.request(getAllDeviceLabels, {}, 'getAggregateBy');

    return data[0].aggregate.labels;
  }

  /**
   *
   * @param {DeviceFilters} filters
   * @param {Pagination} pagination
   * @returns {Promise<{
   *  getDevices: Device[]
   *  getAggregate: AggregateCounters
   * }>}
   */
  async getDevicesWithoutAggregate(filters, pagination) {
    const variables = {
      ...filters,
      ...pagination,
    };

    return this.request(getDevicesWithoutAggregate, variables, 'getDevices');
  }

  /**
   *
   * @returns {Promise<Number>}
   */
  async getDeviceLicenseCount() {
    const data = await this.request(getDeviceLicenseCountQuery, {}, 'me');
    return data?.company?.deviceLicenseCount || 0;
  }

  /**
   *
   * @param {UpdateDeviceByUserInput[]} inputs
   */
  async updateDevices(inputs) {
    return Promise.allSettled(inputs.map(input => this.request(UpdateDevicesQuery, { input }, 'updateDevice')));
  }
}
